import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import dialogConfirm from 'components/dialogConfirm'
import FbButton from 'components/FbUI/FbButton'
import { TextPill } from 'components/FbUI/TextPill'
import {
  Invitation,
  INVITATION_STATUS_ACCEPTED,
  INVITATION_STATUS_EXPIRED,
  INVITATION_STATUS_PENDING,
} from 'models/user'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { useDebounce } from 'use-debounce'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import * as S from './styles'

export function InvitationsTable() {
  const api = apiService()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 300)
  const [resendingId, setResendingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['invitations', debouncedSearch],
    queryFn: async () => {
      return await api.listInvitations({
        search: debouncedSearch || undefined,
        page_size: 100,
      })
    },
    placeholderData: keepPreviousData,
  })

  const handleResend = async (id: number) => {
    setResendingId(id)
    try {
      await api.resendInvitation(id)
      toast.success('Invitation resent successfully!')
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    } catch {
      toast.error('Failed to resend invitation')
    } finally {
      setResendingId(null)
    }
  }

  const handleDelete = async (id: number) => {
    const confirmed = await dialogConfirm(
      'Are you sure you want to delete this invitation? This cannot be undone.',
      'Delete Invitation'
    )
    if (!confirmed) return
    setDeletingId(id)
    try {
      await api.deleteInvitation(id)
      toast.success('Invitation deleted successfully!')
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    } catch {
      toast.error('Failed to delete invitation')
    } finally {
      setDeletingId(null)
    }
  }

  const columns = useMemo<ColumnDef<Invitation, any>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        size: 250,
      },
      {
        accessorKey: 'user_type',
        header: 'User Type',
        size: 120,
      },
      {
        accessorKey: 'rsm_assignment_name',
        header: 'Sample Reviewer',
        size: 180,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: (info) => {
          const value = info.getValue() as string
          if (value === INVITATION_STATUS_ACCEPTED) {
            return (
              <TextPill textColor="#13462C" backgroundColor="#D1E8D5">
                Accepted
              </TextPill>
            )
          }
          if (value === INVITATION_STATUS_EXPIRED) {
            return (
              <TextPill textColor="#464646" backgroundColor="#E3E1DA">
                Expired
              </TextPill>
            )
          }
          if (value === INVITATION_STATUS_PENDING) {
            return (
              <TextPill textColor="#7C5300" backgroundColor="#FFF3D6">
                Pending
              </TextPill>
            )
          }
          return value
        },
      },
      {
        accessorKey: 'invited_by_name',
        header: 'Invited By',
        size: 180,
        cell: (info) => info.getValue() ?? '-',
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 150,
        cell: (info) => {
          const value = info.getValue() as string
          return value ? new Date(value).toLocaleDateString() : '-'
        },
      },
      {
        accessorKey: 'expires_at',
        header: 'Expires',
        size: 150,
        cell: (info) => {
          const value = info.getValue() as string
          return value ? new Date(value).toLocaleDateString() : '-'
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 160,
        cell: ({ row }) => {
          const invite = row.original
          if (invite.status === INVITATION_STATUS_ACCEPTED) return null
          const isBusy = resendingId !== null || deletingId !== null
          return (
            <div className="flex gap-2">
              <FbButton
                size="sm"
                variant="secondary"
                onClick={() => handleResend(invite.id)}
                loading={resendingId === invite.id}
                disabled={isBusy}
              >
                Resend
              </FbButton>
              <FbButton
                size="sm"
                variant="danger"
                onClick={() => handleDelete(invite.id)}
                loading={deletingId === invite.id}
                disabled={isBusy}
              >
                Delete
              </FbButton>
            </div>
          )
        },
      },
    ],
    // only resendingId/deletingId changes the UI
    [resendingId, deletingId]
  )

  if (isPending) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <Spinner animation="border" />
      </div>
    )
  }

  const invitations = data?.results ?? []

  return (
    <DataTableContainer>
      <ClientDataTable
        tableHeader={
          <S.SettingsTableHeader>
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-line rounded-md px-3 py-1.5 text-sm w-64"
            />
          </S.SettingsTableHeader>
        }
        height={'100%'}
        data={invitations}
        columns={columns}
      />
    </DataTableContainer>
  )
}
